import { useQuery } from '@tanstack/react-query'
import { Eraser } from 'lucide-react'
import { cn } from '@/lib/cn'
import { formatCurrency } from '@/lib/format'
import { queryKeys } from '@/lib/query-client'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Spinner } from '@/components/ui/Spinner'
import { ErrorState } from '@/components/ui/States'
import { previewAreaSurcharge } from './api'
import { describeEnd, describeEndCharge } from './describe'
import type { TripPins } from './AreaMap'
import styles from './AreaPricingPage.module.css'

interface TripCheckPanelProps {
  trip: TripPins
  enabled: boolean
  onClear: () => void
  onDone: () => void
}

/**
 * Drop a pickup and a drop-off and see what the trip would add — asked of the
 * server, so it answers with the same sub-areas, the same overlaps and the same
 * prices a booking would be charged with. Works while Cairo is priced as one
 * area, which is when it is most needed: checking the drawing before customers
 * see it.
 */
export function TripCheckPanel({ trip, enabled, onClear, onDone }: TripCheckPanelProps) {
  const { pickup, dropoff } = trip
  const both = pickup && dropoff ? { pickup, dropoff } : null

  const preview = useQuery({
    queryKey: both
      ? queryKeys.areaPricing.preview(both.pickup.lat, both.pickup.lng, both.dropoff.lat, both.dropoff.lng)
      : queryKeys.areaPricing.all,
    queryFn: ({ signal }) => previewAreaSurcharge(both!.pickup, both!.dropoff, signal),
    enabled: both !== null,
  })

  const result = both ? preview.data : undefined

  return (
    <Card>
      <CardHeader
        title="Check a trip"
        description="Click the map for the pickup, then the drop-off. Drag either pin to move it."
      />
      <CardBody>
        <div className={styles.panelBody}>
          <dl className={styles.ends}>
            <div className={styles.end}>
              <dt>
                <span className={cn(styles.pinSwatch, styles.pinSwatchPickup)} aria-hidden="true" />
                Pickup
              </dt>
              <dd>
                {result ? (
                  <>
                    <span>{describeEnd(result.pickup)}</span>
                    <span className={styles.rowMeta}>{describeEndCharge(result.pickup)}</span>
                  </>
                ) : (
                  <span className={styles.rowMeta}>{pickup ? 'Placed' : 'Click the map'}</span>
                )}
              </dd>
            </div>
            <div className={styles.end}>
              <dt>
                <span className={cn(styles.pinSwatch, styles.pinSwatchDropoff)} aria-hidden="true" />
                Drop-off
              </dt>
              <dd>
                {result ? (
                  <>
                    <span>{describeEnd(result.dropoff)}</span>
                    <span className={styles.rowMeta}>{describeEndCharge(result.dropoff)}</span>
                  </>
                ) : (
                  <span className={styles.rowMeta}>{dropoff ? 'Placed' : pickup ? 'Click the map' : '—'}</span>
                )}
              </dd>
            </div>
          </dl>

          {both && preview.isPending ? (
            <p className={styles.loading}>
              <Spinner size="sm" label={null} /> Working it out…
            </p>
          ) : null}
          {both && preview.isError ? (
            <ErrorState compact error={preview.error} onRetry={() => void preview.refetch()} />
          ) : null}

          {result ? (
            <div className={styles.total} role="status">
              <span className={styles.totalLabel}>Added to every Cairo package</span>
              <span className={styles.totalValue}>
                {result.totalMinor > 0 ? `+${formatCurrency(result.total)}` : 'Nothing'}
              </span>
            </div>
          ) : null}

          {result && !enabled ? (
            <p className={styles.hint}>
              Cairo is priced as one area at the moment, so customers are not charged this yet.
            </p>
          ) : null}

          <div className={styles.actions}>
            <Button
              variant="ghost"
              iconLeft={<Eraser aria-hidden="true" />}
              onClick={onClear}
              disabled={!pickup && !dropoff}
            >
              Clear pins
            </Button>
            <Button onClick={onDone}>Done</Button>
          </div>
        </div>
      </CardBody>
    </Card>
  )
}
